import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import {
  FormatProvider,
  GroupList,
  IconsProvider,
  LabelsProvider,
  RoleList,
  ThemeCustomizer,
  ThemeSelector,
  UserList,
  useBranding,
  useFormat,
  useLabels,
  useTheme,
} from '@authifi/clique-react-ui';
import type { Group, GroupListColumn, UserListColumn } from '@authifi/clique-react-ui';
import { CustomGroupView } from './CustomGroupView';

// Tokens that are mapped to the active PrimeReact theme in theme.css — these
// move when you switch the preset theme (most visibly light <-> dark).
const themeDrivenSwatches: { label: string; cssVar: string }[] = [
  { label: 'Primary', cssVar: '--clique-primary' },
  { label: 'Surface', cssVar: '--clique-bg' },
  { label: 'Text', cssVar: '--clique-fg' },
  { label: 'Border', cssVar: '--clique-border' },
  { label: 'Sidebar', cssVar: '--clique-sidebar-bg' },
];

// Tokens that are fixed brand constants in theme.css — they do NOT follow the
// preset theme. They only change via the `tokens`/`darkTokens` config (App.tsx)
// or the live editor below.
const brandSwatches: { label: string; cssVar: string }[] = [
  { label: 'Accent', cssVar: '--clique-accent' },
  { label: 'Destructive', cssVar: '--clique-destructive' },
  { label: 'Warning', cssVar: '--clique-warning' },
];

/*
 * DEV REFERENCE — where each playground section's customization is wired.
 * (Intentionally a source comment, not rendered in the UI.)
 *
 *  1. Preset theme         App.tsx (cliqueTheme.storageKey / cssLayer)
 *                          libs/.../providers/ThemeProvider.tsx (PRIMEREACT_THEMES + fetch effect)
 *                          libs/.../components/shared/ThemeSelector.tsx
 *                          libs/.../theme.css (--clique-* ↔ PrimeReact var mappings)
 *  2. Live brand tokens    App.tsx (cliqueTheme.tokens / darkTokens)
 *                          libs/.../providers/ThemeProvider.tsx (tokenCss → tokensToCss())
 *                          libs/.../theme/tokens.ts (CliqueTokens / TOKEN_TO_CSS_VAR)
 *  3. Customize preset     <ThemeCustomizer /> (libs/.../components/shared/ThemeCustomizer.tsx)
 *      (full, persisted)   libs/.../providers/ThemeProvider.tsx (tokenizeThemeCss on load,
 *                          useThemeCustomization, per-preset overrides persisted to
 *                          localStorage, :root:root injection)
 *                          libs/.../theme/tokens.ts (parseThemePalette / curatedTokens /
 *                          paletteOverridesToCss / THEME_RADIUS_OVERRIDE_VAR)
 *  4. Labels               this file (teamLabels + <LabelsProvider>)
 *                          App.tsx (app-wide: <CliqueProvider labels={…}>)
 *                          libs/.../providers/LabelsProvider.tsx (useLabels)
 *  5. Column config        this file (<GroupList columns / toolbarActions>)
 *                          libs/.../components/groups/GroupList.tsx
 *                          libs/.../theme/overrides.ts (ListColumn<T>)
 *  6. Headless             CustomGroupView.tsx (useGroupListController())
 *                          libs/.../controllers/useGroupListController.ts
 *  7. Typography/spacing   App.tsx (cliqueTheme.tokens fontSize/spaceMd/…)
 *                          libs/.../theme/tokens.ts + libs/.../theme.css
 *  8. Locale               App.tsx (cliqueTheme.format.locale)
 *                          libs/.../providers/FormatProvider.tsx (useFormat())
 *  9. Labels + icons       this file (jobFunctionLabels + <LabelsProvider>/<IconsProvider>)
 *                          App.tsx (cliqueTheme.icons) + libs/.../providers/IconsProvider.tsx
 * 10. List slots           this file (<UserList columns / toolbarActions / renderEmpty>)
 *                          libs/.../components/shared + libs/.../theme/overrides.ts
 * 11. Branding             App.tsx (cliqueTheme.branding)
 *                          libs/.../providers/BrandingProvider.tsx (useBranding())
 *                          apps/shell/src/layouts/AppLayout.tsx (consumes useBranding())
 */

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-xl p-5 mb-6"
      style={{ backgroundColor: 'var(--clique-bg)', border: '1px solid var(--clique-border)' }}
    >
      <h3 className="text-lg font-semibold" style={{ color: 'var(--clique-fg)' }}>
        {title}
      </h3>
      <p className="text-sm mb-4" style={{ color: 'var(--clique-fg-secondary)' }}>
        {description}
      </p>
      {children}
    </section>
  );
}

/** Small "revert this section's controls to their initial values" button. */
function ResetButton({ onReset }: { onReset: () => void }) {
  return (
    <button
      type="button"
      onClick={onReset}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs cursor-pointer"
      style={{
        color: 'var(--clique-fg-secondary)',
        border: '1px solid var(--clique-border)',
        borderRadius: 'var(--clique-radius)',
        backgroundColor: 'var(--clique-bg)',
      }}
    >
      <i aria-hidden="true" className="pi pi-refresh" style={{ fontSize: '0.7rem' }} />
      Reset to default
    </button>
  );
}

function Swatch({ label, cssVar }: { label: string; cssVar: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className="inline-block w-6 h-6 rounded"
        style={{
          backgroundColor: `var(${cssVar})`,
          border: '1px solid var(--clique-border)',
        }}
      />
      <span style={{ color: 'var(--clique-fg-secondary)' }}>{label}</span>
    </div>
  );
}

/**
 * Builds the GroupList columns from the *current* label context, so a wrapping
 * LabelsProvider override changes the column headers too — not just the button
 * and search placeholder the library already labels internally.
 */
function LabelledGroupList(props: { onGroupSelect?: (group: Group) => void }) {
  const { group } = useLabels();
  const columns: GroupListColumn[] = [
    {
      field: 'name',
      header: `${group.singularTitle} name`,
      sortable: true,
      className: 'font-semibold',
    },
    {
      field: 'isPrivileged',
      header: 'Access',
      sortable: true,
      body: (g: Group) => (
        <Tag
          value={g.isPrivileged ? 'Privileged' : 'Standard'}
          severity={g.isPrivileged ? 'warning' : 'success'}
        />
      ),
    },
  ];
  return (
    <GroupList
      columns={columns}
      pageSize={5}
      onCreateGroup={() => undefined}
      onGroupSelect={props.onGroupSelect}
    />
  );
}

const teamLabels = {
  group: {
    singular: 'team',
    plural: 'teams',
    singularTitle: 'Team',
    pluralTitle: 'Teams',
    withArticle: 'a team',
  },
};

// Relabels the (previously hardcoded) role list — proving label parity now
// reaches every list, not just groups.
const jobFunctionLabels = {
  role: {
    singular: 'job function',
    plural: 'job functions',
    singularTitle: 'Job Function',
    pluralTitle: 'Job Functions',
    withArticle: 'a job function',
  },
};

const localeOptions = [
  { label: 'Browser default', value: '' },
  { label: 'English (US)', value: 'en-US' },
  { label: 'British English', value: 'en-GB' },
  { label: 'French', value: 'fr-FR' },
  { label: 'German', value: 'de-DE' },
  { label: 'Japanese', value: 'ja-JP' },
];

// Converts a computed `rgb()/rgba()` color (what getComputedStyle returns) into
// the `#rrggbb` form an <input type="color"> requires.
function rgbToHex(value: string): string {
  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (!match) return value;
  const [r, g, b] = match[1].split(',').map((part) => parseInt(part.trim(), 10));
  const toHex = (n: number) => (Number.isFinite(n) ? n : 0).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Resolves a `--clique-*` color variable to a concrete `#rrggbb` value. Reading
 * the custom property directly returns its declared (possibly `var(...)`) text,
 * so we attach a hidden probe element that *uses* the variable and read its
 * fully-resolved computed color. This is how the Section 2 inputs default to the
 * active preset's real colors instead of a hardcoded hex.
 */
function readThemeColor(cssVar: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const probe = document.createElement('span');
  probe.style.color = `var(${cssVar}, ${fallback})`;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return rgbToHex(resolved) || fallback;
}

// Fallback values for the live controls (used only if a CSS var can't be read,
// e.g. before theme CSS loads). The colors below mirror theme.css defaults; the
// inputs otherwise default to the *active preset's* resolved colors at runtime.
const SECTION_2_DEFAULTS = {
  primary: '#3b82f6',
  accent: '#ea580c',
  destructive: '#ef4444',
  radius: 8,
};
const SECTION_7_DEFAULTS = {
  fontSize: 14,
  spaceMd: 16,
};
const SECTION_8_DEFAULT_LOCALE = '';

const SAMPLE_DATE = '2026-03-14T09:30:00.000Z';

/** Reads the active FormatProvider and renders the same instant several ways. */
function FormattedDateSample() {
  const { formatDate, formatDateTime, locale } = useFormat();
  return (
    <div className="text-sm" style={{ color: 'var(--clique-fg)' }}>
      <p>
        <strong>Locale:</strong> {locale || 'browser default'}
      </p>
      <p>
        <strong>formatDate:</strong> {formatDate(SAMPLE_DATE)}
      </p>
      <p>
        <strong>formatDateTime:</strong> {formatDateTime(SAMPLE_DATE)}
      </p>
    </div>
  );
}

export function PlaygroundPage() {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { appName, logo, supportUrl, footer } = useBranding();
  const { currentTheme, isDark } = useTheme();

  // Live brand-token overrides, applied as scoped CSS custom properties on a
  // wrapper. This demonstrates the `tokens` path that the theme selector does
  // not exercise.
  const [primary, setPrimary] = useState(SECTION_2_DEFAULTS.primary);
  const [accent, setAccent] = useState(SECTION_2_DEFAULTS.accent);
  const [destructive, setDestructive] = useState(SECTION_2_DEFAULTS.destructive);
  const [radius, setRadius] = useState(SECTION_2_DEFAULTS.radius);

  // The "default" for these inputs is the active preset's resolved color. Read
  // them from the live theme so they track whatever preset / primeTokens are in
  // effect, rather than a baked-in hex.
  const applyPresetColors = useCallback(() => {
    setPrimary(readThemeColor('--clique-primary', SECTION_2_DEFAULTS.primary));
    setAccent(readThemeColor('--clique-accent', SECTION_2_DEFAULTS.accent));
    setDestructive(readThemeColor('--clique-destructive', SECTION_2_DEFAULTS.destructive));
  }, []);

  // Sync to the preset on mount and whenever the preset theme changes (switching
  // presets re-establishes the baseline these inputs start from).
  useEffect(() => {
    applyPresetColors();
  }, [applyPresetColors, currentTheme, isDark]);

  const resetSection2 = useCallback(() => {
    applyPresetColors();
    setRadius(SECTION_2_DEFAULTS.radius);
  }, [applyPresetColors]);

  const liveTokenStyle = {
    '--clique-primary': primary,
    '--clique-accent': accent,
    '--clique-destructive': destructive,
    '--clique-radius': `${radius}px`,
    borderRadius: `${radius}px`,
    border: '1px solid var(--clique-border)',
    backgroundColor: 'var(--clique-bg-subtle)',
  } as CSSProperties;

  // Live typography + spacing token overrides (new token groups).
  const [fontSize, setFontSize] = useState(SECTION_7_DEFAULTS.fontSize);
  const [spaceMd, setSpaceMd] = useState(SECTION_7_DEFAULTS.spaceMd);

  const resetSection7 = useCallback(() => {
    setFontSize(SECTION_7_DEFAULTS.fontSize);
    setSpaceMd(SECTION_7_DEFAULTS.spaceMd);
  }, []);
  const typographyStyle = {
    '--clique-font-size': `${fontSize}px`,
    '--clique-space-md': `${spaceMd}px`,
    fontSize: 'var(--clique-font-size)',
    padding: 'var(--clique-space-md)',
    border: '1px solid var(--clique-border)',
    borderRadius: 'var(--clique-radius)',
    backgroundColor: 'var(--clique-bg-subtle)',
  } as CSSProperties;

  // Locale for the formatting demo.
  const [locale, setLocale] = useState(SECTION_8_DEFAULT_LOCALE);

  const handleGroupSelect = useCallback(
    (group: Group) => {
      navigate(`/groups/${group.id}`);
    },
    [navigate],
  );

  return (
    <div>
      <Toast ref={toast} />
      <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--clique-fg)' }}>
        Customization Playground
      </h2>
      <p className="mb-6" style={{ color: 'var(--clique-fg-secondary)' }}>
        A single page that exercises every customization layer the library offers: appearance (theme
        + brand tokens), terminology (labels), and structure (column config + headless controllers).
      </p>

      <Section
        title="1. Appearance — preset theme"
        description="Switch the preset theme (try a dark one) and watch these theme-driven tokens and every PrimeReact component update. The brand tokens below are fixed constants and do NOT follow the preset — see section 2 to change them live."
      >
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <ThemeSelector className="text-sm" />
          <span className="text-sm" style={{ color: 'var(--clique-fg-muted)' }}>
            Active: <strong>{currentTheme}</strong> ({isDark ? 'dark' : 'light'})
          </span>
          <span
            className="flex items-center gap-2 text-sm"
            style={{ color: 'var(--clique-fg-muted)' }}
          >
            Branding: {logo}
            {appName}
          </span>
        </div>
        <p
          className="text-xs uppercase tracking-wide mb-2"
          style={{ color: 'var(--clique-fg-tertiary)' }}
        >
          Theme-driven tokens (follow the preset)
        </p>
        <div className="flex flex-wrap gap-3 mb-4">
          {themeDrivenSwatches.map((swatch) => (
            <Swatch key={swatch.cssVar} label={swatch.label} cssVar={swatch.cssVar} />
          ))}
        </div>
        <p
          className="text-xs uppercase tracking-wide mb-2"
          style={{ color: 'var(--clique-fg-tertiary)' }}
        >
          Brand tokens (fixed constants — set via config, not the preset)
        </p>
        <div className="flex flex-wrap gap-3">
          {brandSwatches.map((swatch) => (
            <Swatch key={swatch.cssVar} label={swatch.label} cssVar={swatch.cssVar} />
          ))}
        </div>
      </Section>

      <Section
        title="2. Appearance — live brand tokens"
        description="The inputs start from the active preset's resolved colors (--clique-primary / -accent / -destructive). Editing them writes --clique-* custom properties onto the wrapper below — exactly like the `tokens` prop on CliqueThemeProvider — so everything inside the box recolors instantly. Switching the preset in section 1 re-seeds these defaults; Reset to default restores the current preset's colors."
      >
        <div className="flex flex-wrap items-center gap-5 mb-4">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--clique-fg)' }}>
            Primary
            <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--clique-fg)' }}>
            Accent
            <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--clique-fg)' }}>
            Destructive
            <input
              type="color"
              value={destructive}
              onChange={(e) => setDestructive(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--clique-fg)' }}>
            Radius {radius}px
            <input
              type="range"
              min={0}
              max={20}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
            />
          </label>
          <ResetButton onReset={resetSection2} />
        </div>
        <div style={liveTokenStyle} className="p-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button
              type="button"
              className="px-3 py-1.5 text-sm cursor-pointer"
              style={{
                backgroundColor: 'var(--clique-primary)',
                color: 'var(--clique-primary-fg)',
                borderRadius: 'var(--clique-radius)',
              }}
            >
              Primary action
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm cursor-pointer"
              style={{
                backgroundColor: 'var(--clique-destructive)',
                color: '#fff',
                borderRadius: 'var(--clique-radius)',
              }}
            >
              Delete
            </button>
            <span
              className="px-2 py-1 text-xs"
              style={{
                backgroundColor: 'var(--clique-accent)',
                color: '#fff',
                borderRadius: 'var(--clique-radius)',
              }}
            >
              Accent badge
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <Swatch label="Primary" cssVar="--clique-primary" />
            <Swatch label="Accent" cssVar="--clique-accent" />
            <Swatch label="Destructive" cssVar="--clique-destructive" />
          </div>
        </div>
      </Section>

      <Section
        title="3. Appearance — customize the preset (global, saved, any preset)"
        description="The reusable <ThemeCustomizer /> component (from the library) lets end users recolor the active preset. Edits apply globally — sidebar, every PrimeReact widget, links, and the components below — and are saved per preset (localStorage) until Reset to default. Works for ANY preset: switch the theme in section 1 and the controls re-read its palette. Note: colors are edited per palette-slot, so a color shared by several roles (e.g. white used by both the card surface and button text) moves together."
      >
        <p className="text-sm mb-3" style={{ color: 'var(--clique-fg-muted)' }}>
          Editing preset: <strong>{currentTheme}</strong>
        </p>

        <ThemeCustomizer className="mb-4" />

        <div
          className="p-4"
          style={{
            border: '1px solid var(--clique-border)',
            borderRadius: 'var(--clique-radius)',
            backgroundColor: 'var(--clique-bg-subtle)',
          }}
        >
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Button label="Save" icon="pi pi-check" size="small" />
            <Button label="Cancel" icon="pi pi-times" size="small" outlined />
            <Button label="Delete" icon="pi pi-trash" size="small" severity="danger" />
            <InputText placeholder="Search…" />
          </div>
          <GroupList
            columns={[
              { field: 'name', header: 'Group', sortable: true, className: 'font-semibold' },
              {
                field: 'isPrivileged',
                header: 'Access',
                body: (g: Group) => <Tag value={g.isPrivileged ? 'Privileged' : 'Standard'} />,
              },
            ]}
            pageSize={3}
          />
        </div>
      </Section>

      <Section
        title="4. Terminology — labels override"
        description='The same list rendered twice. The right one is wrapped in a LabelsProvider that renames "group" → "team", which now changes the column header, the search placeholder, and the New button — no component changes.'
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <p
              className="text-xs uppercase tracking-wide mb-2"
              style={{ color: 'var(--clique-fg-tertiary)' }}
            >
              Default labels
            </p>
            <LabelledGroupList onGroupSelect={handleGroupSelect} />
          </div>
          <div>
            <p
              className="text-xs uppercase tracking-wide mb-2"
              style={{ color: 'var(--clique-fg-tertiary)' }}
            >
              Relabelled as "Teams"
            </p>
            <LabelsProvider labels={teamLabels}>
              <LabelledGroupList onGroupSelect={handleGroupSelect} />
            </LabelsProvider>
          </div>
        </div>
      </Section>

      <Section
        title="5. Structure — column config (low-code)"
        description="A reordered, relabelled column set with a custom cell renderer, passed via the `columns` prop. Structural change, minimal code, still the library's component."
      >
        <GroupList
          columns={[
            { field: 'id', header: '#', sortable: true, style: { width: '70px' } },
            { field: 'name', header: 'Group name', sortable: true, className: 'font-semibold' },
            {
              field: 'isPrivileged',
              header: 'Access',
              sortable: true,
              body: (group: Group) => (
                <Tag
                  value={group.isPrivileged ? 'Privileged' : 'Standard'}
                  severity={group.isPrivileged ? 'warning' : 'success'}
                  icon={group.isPrivileged ? 'pi pi-star' : 'pi pi-check'}
                />
              ),
            },
          ]}
          onGroupSelect={handleGroupSelect}
          pageSize={5}
          toolbarActions={<Button label="Export" icon="pi pi-download" size="small" outlined />}
        />
      </Section>

      <Section
        title="6. Structure — headless controller (full control)"
        description="No library component at all: this card wall is built in the app from useGroupListController, reusing the library's data, search, and pagination logic."
      >
        <CustomGroupView onSelect={handleGroupSelect} />
      </Section>

      <Section
        title="7. Appearance — typography & spacing tokens"
        description="New token groups beyond color: --clique-font-size and --clique-space-* (plus font weights, line height, focus ring, motion). These inputs write the variables onto the wrapper below."
      >
        <div className="flex flex-wrap items-center gap-5 mb-4">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--clique-fg)' }}>
            Font size {fontSize}px
            <input
              type="range"
              min={11}
              max={22}
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
            />
          </label>
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--clique-fg)' }}>
            Spacing (md) {spaceMd}px
            <input
              type="range"
              min={4}
              max={40}
              value={spaceMd}
              onChange={(e) => setSpaceMd(Number(e.target.value))}
            />
          </label>
          <ResetButton onReset={resetSection7} />
        </div>
        <div style={typographyStyle}>
          <p style={{ fontWeight: 'var(--clique-font-weight-bold)' } as CSSProperties}>
            The quick brown fox jumps over the lazy dog.
          </p>
          <p style={{ color: 'var(--clique-fg-secondary)' }}>
            Body text scales with <code>--clique-font-size</code>; the padding around this box is
            driven by <code>--clique-space-md</code>.
          </p>
        </div>
      </Section>

      <Section
        title="8. Localization — locale-aware date formatting"
        description="Wrap any subtree in a FormatProvider to control how the library renders dates. Switch the locale and watch the same instant reformat. List columns (Created / Last Login) use this too."
      >
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm" style={{ color: 'var(--clique-fg)' }}>
            Locale
          </span>
          <Dropdown
            value={locale}
            options={localeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setLocale(e.value as string)}
            className="text-sm"
          />
          <ResetButton onReset={() => setLocale(SECTION_8_DEFAULT_LOCALE)} />
        </div>
        <FormatProvider config={{ locale: locale || undefined }}>
          <FormattedDateSample />
        </FormatProvider>
      </Section>

      <Section
        title="9. Terminology + icons — parity on every list"
        description='The role list (previously hardcoded "access roles") now reads labels and icons. The right column is wrapped in a LabelsProvider that renames role → "job function" and an IconsProvider that swaps the create + privileged icons — no component changes.'
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <p
              className="text-xs uppercase tracking-wide mb-2"
              style={{ color: 'var(--clique-fg-tertiary)' }}
            >
              Default labels & icons
            </p>
            <RoleList pageSize={5} onCreateRole={() => undefined} />
          </div>
          <div>
            <p
              className="text-xs uppercase tracking-wide mb-2"
              style={{ color: 'var(--clique-fg-tertiary)' }}
            >
              Relabelled + custom icons
            </p>
            <LabelsProvider labels={jobFunctionLabels}>
              <IconsProvider icons={{ create: 'pi pi-briefcase', role: 'pi pi-briefcase' }}>
                <RoleList pageSize={5} onCreateRole={() => undefined} />
              </IconsProvider>
            </LabelsProvider>
          </div>
        </div>
      </Section>

      <Section
        title="10. Structure — columns, toolbar & render slots on any list"
        description="The same column/toolbar customization that GroupList has is now on every list. Here UserList gets a reordered column set, a toolbar action, and a custom empty-state render slot."
      >
        <UserList
          pageSize={5}
          columns={
            [
              { field: 'displayName', header: 'User', sortable: true, className: 'font-semibold' },
              { field: 'email', header: 'Email', sortable: true },
              {
                field: 'provider',
                header: 'IdP',
                body: (u) => <Tag value={u.provider} severity="info" />,
              },
            ] satisfies UserListColumn[]
          }
          toolbarActions={<Button label="Invite" icon="pi pi-envelope" size="small" outlined />}
          renderEmpty={() => (
            <div className="py-10 text-center" style={{ color: 'var(--clique-fg-secondary)' }}>
              A fully custom empty state — supplied via the <code>renderEmpty</code> slot.
            </div>
          )}
        />
      </Section>

      <Section
        title="11. Branding — extended identity"
        description="Branding now carries more than a logo and app name. The host chrome can surface a support URL and footer, and apply a favicon — all from the same config object consumed via useBranding()."
      >
        <div className="text-sm" style={{ color: 'var(--clique-fg)' }}>
          <p className="flex items-center gap-2">
            <strong>App:</strong> {logo} {appName}
          </p>
          <p>
            <strong>Support URL:</strong>{' '}
            {supportUrl ? (
              <a href={supportUrl} style={{ color: 'var(--clique-primary)' }}>
                {supportUrl}
              </a>
            ) : (
              <span style={{ color: 'var(--clique-fg-tertiary)' }}>not set</span>
            )}
          </p>
          <div className="mt-2">
            <strong>Footer:</strong>{' '}
            {footer ?? <span style={{ color: 'var(--clique-fg-tertiary)' }}>not set</span>}
          </div>
        </div>
      </Section>
    </div>
  );
}
